import { Calendar } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "shared/ui/shadcn/tooltip";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import { BookedMeetingCalendarEventI } from "shared/lib/interfaces/meeting-acceptance";
import { clsxMerge, safeGetDate } from "shared/lib/helpers";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import {
  CALENDAR_ACCEPTANCE_STATUS_COLORS_MAP,
  CALENDAR_ACCEPTANCE_STATUS_LABELS_MAP,
} from "shared/lib/constants/meeting-acceptance";
import { Avatar, AvatarFallback } from "shared/ui/shadcn/avatar";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { DATE_FORMAT_WITHOUT_TZ } from "shared/lib/constants/time";

import { BookedMeetingCalendarEventStates } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/states";
import { useQueryFetchBookedMeetingCalendarEventForAccount } from "@/modules/pipeline/account-details/data/queries";
import { getFallbackTextForBookedMeetingEventAttendeeAvatar } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/utils";
import { JoinMeetingLink } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/join-meeting-link";
import { useMutationRequestSyncCalendarEventForAccount } from "@/modules/pipeline/account-details/data/mutations";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { ACCOUNTS_DETAILS_TABS } from "@/modules/pipeline/account-details/constants";

const SIZES = { SM: "sm", FULL: "full" } as const;
type SizeI = ValueOfObjectFields<typeof SIZES>;

interface BookedMeetingCalendarEventPropsI extends PropsWithClassNameI {
  campaignId: string | undefined;
  accountId: string | undefined;
  containerClassName?: string;
  size?: SizeI;
}

export const BookedMeetingCalendarEvent = ({
  accountId,
  campaignId,
  className,
  containerClassName,
  size = SIZES.FULL,
}: BookedMeetingCalendarEventPropsI) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          role="button"
          className={clsxMerge(
            "flex cursor-pointer items-center gap-1 p-0 text-[#4474E3] typography-body-4",
            className
          )}
        >
          <Calendar size={16} />
          {size === SIZES.FULL && " Meeting status"}
        </div>
      </TooltipTrigger>

      <TooltipPortal>
        <TooltipContent
          className={clsxMerge(
            "rounded-xl bg-white/80 text-black backdrop-blur",
            "shadow-[2px-2px-6px_0_rgba(0,0,0,0.12)]",
            containerClassName
          )}
        >
          <BookedMeetingCalendarEventCard
            campaignId={campaignId}
            accountId={accountId}
          />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  </TooltipProvider>
);

const BookedMeetingCalendarEventCard = ({
  accountId,
  campaignId,
}: BookedMeetingCalendarEventPropsI) => {
  const bookedMeetingCalendarEventApi =
    useQueryFetchBookedMeetingCalendarEventForAccount(campaignId, accountId);
  const { mutateAsync, isPending } =
    useMutationRequestSyncCalendarEventForAccount();

  // If the context is not undefined, we're on the contact details sidebar.
  // This is useful to reload account details if we detect that there are changes
  // to the meeting from the calendar sync (like canceled or rescheduled).
  const accountDetailsContext = useAccountDetailsContext();

  const handleSyncCalendarEvent = async () => {
    try {
      const event = await mutateAsync({ accountId, campaignId });

      const isScheduled = Boolean(!bookedMeetingCalendarEventApi.data && event);

      const isRescheduled = Boolean(
        accountDetailsContext.accountStatus?.meeting_time &&
        event &&
        accountDetailsContext.accountStatus.meeting_time !== event.meeting_date
      );

      const isCanceled = Boolean(
        bookedMeetingCalendarEventApi.data && event === null
      );

      // Trigger account update if the meeting was scheduled, rescheduled or canceled
      if (isScheduled || isRescheduled || isCanceled) {
        accountDetailsContext?.onAccountUpdate();

        if (accountDetailsContext.activeTab === ACCOUNTS_DETAILS_TABS.HISTORY) {
          accountDetailsContext?.accountHistoryData?.reloadData();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <DataStatesWrapper
      api={bookedMeetingCalendarEventApi}
      loading={<BookedMeetingCalendarEventStates.Pending />}
    >
      <BookedMeetingCalendarEventDetails
        meeting={bookedMeetingCalendarEventApi.data}
        handleSyncCalendarEvent={handleSyncCalendarEvent}
        isSyncingCalendarEvent={isPending}
      />
    </DataStatesWrapper>
  );
};

interface BookedMeetingCalendarEventCardPropsI {
  meeting: BookedMeetingCalendarEventI | undefined | null;
  handleSyncCalendarEvent: () => Promise<void>;
  isSyncingCalendarEvent: boolean;
}

const BookedMeetingCalendarEventDetails = ({
  meeting,
  handleSyncCalendarEvent,
  isSyncingCalendarEvent,
}: BookedMeetingCalendarEventCardPropsI) => {
  return (
    <div className="flex max-w-[400px] flex-col gap-2 p-2">
      <h3 className="max-w-[200px] typography-body-3-medium">
        Calendar event details
      </h3>

      {meeting && meeting.event_exists ? (
        <>
          {meeting.meeting_date && (
            <p className="typography-body-5">
              Scheduled on{" "}
              {safeGetDate(meeting.meeting_date, DATE_FORMAT_WITHOUT_TZ)}
            </p>
          )}

          <JoinMeetingLink conferenceUrl={meeting.conference_url} />

          <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto">
            {meeting.attendees.map((attendee, idx) => {
              return (
                <div key={idx} className="flex gap-2">
                  <Avatar>
                    <AvatarFallback>
                      {getFallbackTextForBookedMeetingEventAttendeeAvatar(
                        attendee
                      )}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex max-w-[90%] flex-col gap-1 typography-body-4">
                    <div className="flex flex-col">
                      {attendee.name && (
                        <span className="truncate" title={attendee.name}>
                          {attendee.name}
                        </span>
                      )}

                      <span className="truncate" title={attendee.email}>
                        {attendee.email}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 typography-body-5">
                      {attendee.is_organizer && (
                        <span className="text-muted-foreground">
                          Organizer &middot;
                        </span>
                      )}
                      <span
                        className={
                          CALENDAR_ACCEPTANCE_STATUS_COLORS_MAP[
                            attendee.acceptance_status
                          ]
                        }
                      >
                        {
                          CALENDAR_ACCEPTANCE_STATUS_LABELS_MAP[
                            attendee.acceptance_status
                          ]
                        }
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-muted-foreground typography-body-5">
            Last updated on{" "}
            {safeGetDate(meeting.updated_at, DATE_FORMAT_WITHOUT_TZ)}
          </p>
        </>
      ) : (
        "No calendar event found for this meeting"
      )}

      <button
        disabled={isSyncingCalendarEvent}
        onClick={handleSyncCalendarEvent}
        className="flex items-center gap-2 text-[#4474E3] typography-body-5"
      >
        {isSyncingCalendarEvent
          ? "Syncing calendar event..."
          : "Request an update"}
      </button>
    </div>
  );
};
